import React, { useEffect, useRef, useState } from "react";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { connect, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { gsap } from "gsap";


import {
  setCodeEntered,
  setLoginMode,
  setOTPVerified,
  setPhoneNumber,
} from "../../../store/slices/userSlice";
import { toggleOverlay } from "../../../store/slices/headerSlice";
import { fetchUserAsync, setToken } from "../../../features/auth/authSlice";
import toast from "react-hot-toast";
import { CircularProgress } from "@mui/material";

function AutoLoginPage({
  user,
  setLoginMode,
  setPhoneNumber,
  toggleOverlay,
  setToken,
  setShowAutoLogin,
}) {
  // GSAP CODE
  const containerRef = useRef(null);
  const phoneInputRef = useRef(null);
  useEffect(() => {
    // GSAP animation for loading the component
    gsap.fromTo(
      containerRef.current,
      { y: "100%", opacity: 0 },
      { y: "0%", opacity: 1, duration: 0.5, ease: "power3.out" }
    );

    // Focus on the phone number input when the component is opened
    if (phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  }, []);
  const handleCloseTab = () => {
    // GSAP animation for closing the component
    gsap.to(containerRef.current, {
      y: "100%",
      opacity: 0,
      duration: 0.5,
      ease: "power3.in",
      onComplete: () => setShowAutoLogin(false),
    });
  };

  // MAIN CODE
  const { loginMode, phoneNumber } = user;

  const handletoggle = () => {
    if (loginMode === "login") {
      toggleOverlay();
      setPhoneNumber(null);
    } else {
      setLoginMode("login");
      setPhoneNumber(null);
    }
  };

  const dispatch = useDispatch();
  const [otpSent, setOtpSent] = useState(false); // Track OTP sent status
  const [enteredOTP, setEnteredOTP] = useState("");
  const [otpLoading, setOtpLoading] = useState(false); // Track loading state for OTP actions
  const [errors, setErrors] = useState({});

  const handlePhoneCheckAndSendOTP = async (event) => {
    event.preventDefault();
    setOtpLoading(true);
    setErrors({}); // Clear previous errors

    if (phoneNumber?.length !== 10) {
      setErrors({ phone: "Please enter a valid 10-digit phone number." });
      setOtpLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}login_number/${phoneNumber}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        setOtpSent(true);
        toast.success("OTP sent successfully.");
      } else {
        setErrors({
          phone: "Mobile number is not registered with Cyclecircle.",
        });
      }
    } catch (error) {
      // console.error("API error:", error.message);
      toast.error("Please try again later.");
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    if (phoneNumber?.length < 10) {
      setOtpSent(false);
      setErrors({});
    }
  }, [phoneNumber]);

  const handleVerifyOTP = async () => {
    setOtpLoading(true);
    setErrors({});
    if (enteredOTP.length === 6) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}login_number_otp/${phoneNumber}/${enteredOTP}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success("OTP verified successfully.");
          document.cookie = `UserID=${data.UserID}`;
          document.cookie = `token=${data.token}`;
          await dispatch(setToken(data.token));
          dispatch(fetchUserAsync(data.UserID));
          setPhoneNumber(null);
          handleCloseTab();
        } else {
          setErrors({ OTP: "Please enter a valid 6-digit OTP." });
          // console.error("API request failed:", response.statusText);
        }
      } catch (error) {
        // console.error(
        //   "An error occurred during the API request:",
        //   error.message
        // );
        toast.error("Please try again later");
      } finally {
        setOtpLoading(false);
      }
    } else {
      setErrors({ OTP: "Please enter a valid 6-digit OTP." });
      setOtpLoading(false);
    }
  };

  // useEffect(() => {
  //   // Prevent background scrolling when overlay is shown
  //   document.body.style.overflow = "hidden";

  //   return () => {
  //     // Re-enable scrolling when overlay is removed
  //     document.body.style.overflow = "auto";
  //   };
  // }, []);

  return (
    <>
      <div
        className="fixed inset-0 bg-black-1 bg-opacity-80 z-[999]"
        onClick={handleCloseTab}
      />

      <div
        ref={containerRef}
        className="w-full fixed bottom-0 left-0 right-0 z-[1000] bg-white-1 flex flex-col p-5 pb-10 rounded-t-2xl shadow-lg"
      >
        <button
          onClick={handleCloseTab}
          className="absolute top-4 right-4 text-black-1"
          aria-label="Close"
        >
          <CloseSharpIcon style={{ fontSize: 20 }} />
        </button>

        <h2 className="mt-6 mb-5 text-[19px] text-black-1/80 font-extrabold tracking-wide">
          {loginMode === "login" ? "Login" : "Sign Up"} to continue
        </h2>

        <form
          onSubmit={handlePhoneCheckAndSendOTP}
          className="w-full flex flex-col"
        >
          <label
            htmlFor="phoneNumber"
            className="text-black-1 text-left text-sm mb-2"
          >
            Mobile Number
          </label>
          <input
            type="text"
            id="phoneNumber"
            name="phoneNumber"
            value={phoneNumber}
            placeholder="Enter mobile number"
            maxLength={10}
            pattern="\d{10}"
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) e.preventDefault();
            }}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className={`w-full ${
              errors.phone ? "mb-2" : "mb-4"
            } p-3 rounded border border-gray-600 bg-white-1 text-black-4 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400`}
            required
          />
          {errors.phone && (
            <p className="text-red-500 text-xs w-full text-left mb-2">
              {errors.phone}
            </p>
          )}

          {otpSent && (
            <>
              <input
                type="text"
                placeholder="Enter OTP"
                value={enteredOTP}
                onChange={(e) =>
                  setEnteredOTP(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                onKeyDown={(e) => {
                  if (
                    e.key === " " ||
                    (isNaN(Number(e.key)) && e.key.length === 1)
                  ) {
                    e.preventDefault();
                  }
                }}
                maxLength={6}
                className={`w-full ${
                  errors.OTP ? "mb-2" : "mb-4"
                } p-3 rounded border border-gray-600 bg-white-1 text-black-4 focus:outline-none focus:ring-2 focus:ring-yellow-400`}
              />
              {errors.OTP && (
                <p className="text-red-500 text-xs w-full text-left mb-2">
                  {errors.OTP}
                </p>
              )}
            </>
          )}

          <button
            type="submit"
            onClick={otpSent ? handleVerifyOTP : handlePhoneCheckAndSendOTP}
            disabled={otpLoading}
            className={`w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-3 rounded mb-4 flex justify-center items-center ${
              otpLoading ? "opacity-75 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            {otpLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : otpSent ? (
              "Verify OTP"
            ) : (
              "Send OTP"
            )}
          </button>
        </form>

        <div className="flex items-center justify-center text-black-1/80 text-sm font-medium my-2">
          <p className="mr-1">
            {loginMode === "login"
              ? "Don't have an account?"
              : "Already have an account?"}
          </p>
          <span
            className="text-yellow-500 font-semibold cursor-pointer hover:underline"
            onClick={() => {
              handletoggle();
              handleCloseTab();
            }}
          >
            {loginMode === "login" ? "Sign up" : "Login"}
          </span>
        </div>

        <div className="text-center text-sm text-gray-600 mt-4">
          <span>
            By continuing, you agree to Cyclecircle's{" "}
            <Link
              to="/privacypolicy"
              className="text-yellow-500 font-semibold hover:underline"
            >
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link
              to="/support"
              className="text-yellow-500 font-semibold hover:underline"
            >
              User Agreement
            </Link>
            .
          </span>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

const mapDispatchToProps = {
  setLoginMode,
  setPhoneNumber,
  setCodeEntered,
  setOTPVerified,
  toggleOverlay,
  setToken,
};

export default connect(mapStateToProps, mapDispatchToProps)(AutoLoginPage);
